import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { fetchWithAuth } from '../lib/api/fetchWithAuth';

interface User {
  id: string;
  email: string;
  role: 'ADMIN' | 'MEMBER';
  brandId: string;
  brand: {
    id: string;
    name: string;
    organizationId: string;
    organization: {
      id: string;
      name: string;
    };
  };
}

interface UserContextType {
  user: User | null;
  isLoading: boolean;
  error: Error | null;
}

const UserContext = createContext<UserContextType>({
  user: null,
  isLoading: true,
  error: null,
});

export function UserProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, getAccessTokenSilently, user: auth0User } = useAuth0();
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const initializeUser = async () => {
      if (!isAuthenticated || !auth0User?.email) {
        setIsLoading(false);
        return;
      }

      try {
        console.log('Initializing user with:', { 
          isAuthenticated, 
          auth0User 
        });
        
        const token = await getAccessTokenSilently();
        
        // First try to create/update the user
        console.log('Creating/updating user...');
        const createResponse = await fetchWithAuth('/api/users', {
          method: 'POST',
          body: JSON.stringify({ email: auth0User.email }),
          token
        });
        console.log('Create/update response:', createResponse);

        // Then fetch the user data
        console.log('Fetching user data...');
        const userData = await fetchWithAuth('/api/users/me', { token });
        console.log('User data:', userData);
        setUser(userData);
      } catch (err) {
        console.error('Error in initializeUser:', err);
        setError(err instanceof Error ? err : new Error('Failed to fetch user'));
      } finally {
        setIsLoading(false);
      }
    };

    initializeUser();
  }, [isAuthenticated, getAccessTokenSilently, auth0User]);

  return (
    <UserContext.Provider value={{ user, isLoading, error }}>
      {children}
    </UserContext.Provider>
  );
}

export const useUser = () => useContext(UserContext); 